import React, {
    FunctionComponent,
    useState,
    useEffect,
    useRef,
    useMemo,
} from "react";
import {
    instanceOfDsqRecord,
    Protocol,
    SingleRaceRecord,
    instanceOfSingleRaceRecord,
    instanceOfTeamRecord,
    Challenge, IppodromItem,
    ClubTabFilterItem,
    ClubRating,
} from "/src/domain/challenges/ChallengeProtocolData";
import {User} from "/src/domain/shared/User";
import {ProtocolView} from "./ProtocolView";
import {ClubsProtocolView} from "./clubProtocol/ClubsProtocolView";
import {ProtocolFilters} from "./ProtocolFilters";
import {useTranslation} from "react-i18next";
import {IppodromView} from "/src/ui/challengeProtocol/ippodrom/IppodromView";

interface ProtocolsContainerProps {
    protocols: Protocol[];
    ippodrom: IppodromItem[];
    currentUser?: User;
    challenge: Challenge;
    clubsRating: ClubRating[]
}

interface ProtocolsExpandState {
    [key: string]: boolean;
}

export const ProtocolsContainer: FunctionComponent<ProtocolsContainerProps> = ({
                                                                                   protocols,
                                                                                   challenge,
                                                                                   currentUser,
                                                                                   ippodrom,
                                                                                   clubsRating,
                                                                               }) => {
    const [expandState, setExpandState] = useState<ProtocolsExpandState>({});
    const protocolsContainerRef = useRef<HTMLDivElement>(null);
    const {t} = useTranslation();

    useEffect(() => {
        const newExpandState: ProtocolsExpandState = {};
        let isUserProtocolFound: boolean = false;

        protocols.forEach((protocol) => {
            if (!currentUser) {
                newExpandState[protocol.title] = true;
                return;
            }

            const userInProtocol = protocol.records.find((record) => {
                if (instanceOfDsqRecord(record) || instanceOfSingleRaceRecord(record)) {
                    return record.participant.id === currentUser.id;
                }
                if (instanceOfTeamRecord(record)) {
                    return record.records.some(
                        (r) => r.participant.id === currentUser.id
                    );
                }
                return false;
            });

            if (userInProtocol) isUserProtocolFound = true;

            newExpandState[protocol.title] = Boolean(userInProtocol);
        });

        if (!isUserProtocolFound) {
            Object.keys(newExpandState).forEach(
                (key) => (newExpandState[key] = true)
            );
        }

        setExpandState(newExpandState);
    }, [protocols]);

    const changeExpandState = (title: string) => {
        const newExpandState: ProtocolsExpandState = {};

        protocols.forEach((category) => {
            newExpandState[category.title] = false;
        });
        newExpandState[title] = !expandState[title];
        setExpandState(newExpandState);
    };

    const clubs: ClubTabFilterItem[] = useMemo(() => {
        const result: ClubTabFilterItem[] = [];

        const pushClub = (r: SingleRaceRecord) => {
            const clubId = r.participant.clubId ? String(r.participant.clubId) : "0";
            const clubName = r.participant.clubName
                ? r.participant.clubName
                : t("withoutClub");
            if (result.findIndex((club) => club.id == clubId) < 0) {
                result.push({
                    id: clubId,
                    name: clubName,
                });
            }
        };

        for (const protocol of protocols) {
            for (const record of protocol.records) {
                if (instanceOfSingleRaceRecord(record)) {
                    pushClub(record);
                }
                if (instanceOfTeamRecord(record)) {
                    record.records.forEach((r) => pushClub(r));
                }
            }
        }

        return result;
    }, [protocols]);

    return (
        <div>
            <ClubsProtocolView
                protocols={protocols}
                challenge={challenge}
                currentUser={currentUser}
                ippodrom={ippodrom}
                protocolsContainerRef={protocolsContainerRef}/>
            <div ref={protocolsContainerRef}>
                <ProtocolFilters clubs={clubs} clubsRating={clubsRating}/>

                {protocols.map((protocol) => (
                    <ProtocolView
                        key={protocol.title}
                        challengeType={challenge.formula}
                        protocol={protocol}
                        isExpand={expandState[protocol.title]}
                        containerRef={protocolsContainerRef}
                        onProtocolClick={() => {
                            changeExpandState(protocol.title);
                        }}
                        currentUser={currentUser}
                    />
                ))}

                <IppodromView items={ippodrom}/>
            </div>
        </div>
    );
};
